import { adminApi } from '@/features/admin';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Camera, Clock, UserPlus, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminApi.getStats(),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Tableau de bord Admin</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Utilisateurs',
      value: stats?.totalUsers || 0,
      description: 'Total',
      icon: Users,
      color: 'text-blue-500',
    },
    {
      title: "Nouveaux aujourd'hui",
      value: stats?.newUsersToday || 0,
      description: 'Inscriptions',
      icon: UserPlus,
      color: 'text-green-500',
    },
    {
      title: 'Photos en attente',
      value: stats?.pendingPhotos || 0,
      description: 'A valider',
      icon: Camera,
      color: 'text-orange-500',
    },
    {
      title: 'RDV en attente',
      value: stats?.pendingMeetings || 0,
      description: 'A traiter',
      icon: Calendar,
      color: 'text-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Tableau de bord Admin</h1>
        <Badge variant="outline" className="text-sm">
          <Clock className="h-3 w-3 mr-1" />
          Derniere mise a jour: maintenant
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-orange-500" />
              Photos a valider
            </CardTitle>
            <CardDescription>
              {stats?.pendingPhotos || 0} photos en attente de validation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/photos">Valider les photos</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-500" />
              Rendez-vous
            </CardTitle>
            <CardDescription>
              {stats?.pendingMeetings || 0} RDV en attente d'attribution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/meetings">Gerer les RDV</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Utilisateurs recents
            </CardTitle>
            <CardDescription>Voir les derniers inscrits et actifs</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/users">Voir les utilisateurs</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Activite recente</CardTitle>
          <CardDescription>Les dernieres actions sur la plateforme</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats?.recentActivity?.length ? (
              stats.recentActivity.map(
                (activity: { id: number; type: string; description: string; time: string }) => (
                  <div key={activity.id} className="flex items-center gap-4">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        activity.type === 'registration'
                          ? 'bg-green-500'
                          : activity.type === 'meeting'
                            ? 'bg-purple-500'
                            : 'bg-blue-500'
                      }`}
                    />
                    <div className="flex-1">
                      <p className="text-sm">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                  </div>
                ),
              )
            ) : (
              <p className="text-muted-foreground text-sm">Aucune activite recente</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
